import React from "react";

import * as Constants from "commons/constants";
import * as utils from "commons/utils";
import { useViewStateDispatcher } from "providers/ViewStateProvider";
import { Div, MainPaper } from "components/containers/Containers";
import QBTypography from "components/QBTypography";
import RequestInfo from "components/modals/RequestInfo";

import { makeStyles } from "@material-ui/core/styles";
import { useMessages, useBranding } from "providers/BrandingProvider";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: 40,
    marginTop: 25,
  },
  sectionTitle: {
    marginTop: 10,
    marginBottom: 10,
  },
  titlesection: {
    marginTop: 10,
    marginBottom: 10,
  },
  paragraph: {
    marginTop: 15,
    marginBottom: 15,
  },
  subsection: {
    marginTop: 20,
  },
  name: {
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  phone: {
    color: theme.palette.primary.dark,
  },
}));

const REFS = {
  team: React.createRef(),
  disclaimers: React.createRef(),
  quotes: React.createRef(),
  bids: React.createRef(),
  offers: React.createRef(),
  orders: React.createRef(),
};

let SCROLL_TIMEOUT = null;

export const HelpView = ({ location }) => {
  const classes = useStyles();
  const dispatchViewStateChange = useViewStateDispatcher();
  const Messages = useMessages();
  const branding = useBranding();

  const isQuotesEnabled =
    process.env.REACT_APP_FEATURES_QUOTES === "true" &&
    utils.hasNonEmptyValue(Messages.MESSAGE.DISCLAIMERS[Constants.TRADE_TYPE.PRICE_QUOTES]);
  const isBidsEnabled =
    process.env.REACT_APP_FEATURES_BIDS === "true" &&
    utils.hasNonEmptyValue(Messages.MESSAGE.DISCLAIMERS[Constants.TRADE_TYPE.BIDS_RFQ]);
  const isOfferEnabled =
    process.env.REACT_APP_FEATURES_OFFER === "true" &&
    utils.hasNonEmptyValue(Messages.MESSAGE.DISCLAIMERS[Constants.TRADE_TYPE.BONDS_FOR_SALE]);

  // Returns an array of QBTypographies so that we can effectively render many paragraphs as
  // well as make it manageable in the messages file to store the paragraph breaks.
  const QBTypographyParagraphs = ({ textArray }) => {
    const qbTypographies = textArray.map((text, index) => {
      return (
        <QBTypography key={index} className={classes.paragraph}>
          {text}
        </QBTypography>
      );
    });
    return <div>{qbTypographies}</div>;
  };

  React.useEffect(() => {
    dispatchViewStateChange({
      state: { activeView: Constants.VIEW.HELP },
      view: Constants.VIEW.HELP,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (SCROLL_TIMEOUT) {
      SCROLL_TIMEOUT = null;
    }

    SCROLL_TIMEOUT = window.setTimeout(function () {
      const hash = location.hash.substring(1);
      if (REFS.hasOwnProperty(hash) && REFS[hash].current) {
        REFS[hash].current.scrollIntoView();
      }
    }, 10);

    return function () {
      SCROLL_TIMEOUT = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  return (
    <Div>
      <MainPaper style={{ padding: 0 }}>
        <Div style={{ overflow: "auto", padding: "10px 20px" }}>
          <div className={classes.section}>
            <QBTypography className={classes.paragraph} component="div" variant="body2">
              The Help Desk is available to serve you by answering questions and providing additional
              information.For questions, additional information, if you have an immediate need to place or
              resolve a trade, or questions about a specific bond please call the Help Desk at{" "}
              <span className={classes.phone}>{Messages.LABEL.TRADE_DESK_PHONE}</span> or{" "}
              <RequestInfo variant="hyperlink" linkLabel={`${Messages.MESSAGE.SEND_MESSAGE}.`} />
            </QBTypography>
          </div>
          {branding.isQwickBonds && (
            <div className={classes.section}>
              <QBTypography
                className={classes.sectionTitle}
                variant="h3"
                color="primary"
                gutterBottom
                ref={REFS.team}
              >
                QwickBonds Team
              </QBTypography>
              <QBTypography className={classes.titlesection} component="div" variant="body2">
                <div>
                  <span className={classes.name}>Aaron Kehoe</span>
                </div>
                <div>
                  Managing Director, Head of QwickBonds <br />
                  MCAP, LLC.
                </div>
                <div>
                  <span className={classes.phone}>312-506-8755</span>{" Direct"}
                </div>
                <div>
                  <span>{utils.formatEmail("aaron.kehoe@qwickbonds.com")}</span>
                </div>
              </QBTypography>
              <QBTypography className={classes.titlesection} component="div" variant="body2">
                <div>
                  <span className={classes.name}>Steve Levy</span>
                </div>
                <div>
                  Director <br />
                  MCAP, LLC.
                </div>
                <div>
                  <span className={classes.phone}>212-607-8184</span>{" Direct"}
                </div>
                <div>
                  <span>{utils.formatEmail("steve.levy@qwickbonds.com")}</span>
                </div>
              </QBTypography>
              <QBTypography className={classes.titlesection} component="div" variant="body2">
                <div>
                  <span className={classes.name}>Robert Scherne</span>
                </div>
                <div>
                  Associate <br />
                  MCAP, LLC.
                </div>
                <div>
                  <span className={classes.phone}>646-298-3006</span>{" Direct"}
                </div>
                <div>
                  <span>{utils.formatEmail("robert.scherne@qwickbonds.com")}</span>
                </div>
              </QBTypography>
            </div>
          )}
          <div className={classes.section}>
            <QBTypography
              className={classes.sectionTitle}
              variant="h3"
              color="primary"
              gutterBottom
              ref={REFS.disclaimers}
            >
              {Messages.LABEL.DISCLAIMERS}
            </QBTypography>
            <QBTypography className={classes.paragraph} style={{ fontSize: "1.0rem" }}>
              {utils.formatHyperlink(
                `${process.env.PUBLIC_URL}/documents/QwickBonds_Disclaimer.pdf`,
                "Important Information and Disclaimers",
              )}
            </QBTypography>

            <QBTypography className={classes.paragraph}>{Messages.MESSAGE.DISCLAIMERS.GENERAL1}</QBTypography>
            <QBTypography className={classes.paragraph}>{Messages.MESSAGE.DISCLAIMERS.GENERAL2}</QBTypography>
            <QBTypography className={classes.paragraph}>{Messages.MESSAGE.DISCLAIMERS.GENERAL3}</QBTypography>
            <QBTypography className={classes.paragraph}>{Messages.MESSAGE.DISCLAIMERS.GENERAL4}</QBTypography>
            <QBTypography className={classes.paragraph}>
              Please refer to the{" "}
              {utils.formatHyperlink(
                "https://livewebapp.qwickrate.com/qwickrate/XSP/brokeredsecurities/cds/CD_disclosure.pdf",
                "Brokered CD Disclosure Statement",
              )}{" "}
              for important disclosures about brokered CDs, which should be read carefully before purchasing a
              CD. Finra's BrokerCheck for {Messages.BRANDING.PROVIDER_NAME}. can be found{" "}
              {utils.formatHyperlink(
                "https://brokercheck.finra.org/firm/summary/139515",
                "here",
                "FINRA BrokerCheck",
              )}
              , pursuant to Finra Rule 2210, By accessing this website and our services, you agree to the
              terms and conditions stipulated in our{" "}
              {utils.formatHyperlink(`${process.env.PUBLIC_URL}/documents/Privacy.pdf`, "Privacy Policy")}.
            </QBTypography>

            <div className={classes.subsection}>
              <QBTypography variant="h6" color="secondary" ref={REFS.orders}>
                Orders
              </QBTypography>

              <QBTypography className={classes.paragraph}>
                {Messages.MESSAGE.DISCLAIMERS.ORDERS1}
              </QBTypography>
              <QBTypography className={classes.paragraph}>
                {Messages.MESSAGE.DISCLAIMERS.ORDERS2}
              </QBTypography>
            </div>

            {isQuotesEnabled && (
              <div className={classes.subsection}>
                <QBTypography variant="h6" color="secondary" ref={REFS.quotes}>
                  {Messages.LABEL.PRICE_QUOTES}:
                </QBTypography>

                <QBTypography className={classes.paragraph}>
                  {Messages.MESSAGE.DISCLAIMERS[Constants.TRADE_TYPE.PRICE_QUOTES].EXTENDED}
                </QBTypography>
              </div>
            )}
            {isBidsEnabled && (
              <div className={classes.subsection}>
                <QBTypography variant="h6" color="secondary" ref={REFS.bids}>
                  {Messages.LABEL.BIDS_RFQ}:
                </QBTypography>

                <QBTypography className={classes.paragraph}>
                  {Messages.MESSAGE.DISCLAIMERS[Constants.TRADE_TYPE.BIDS_RFQ].EXTENDED}
                </QBTypography>
              </div>
            )}

            {isOfferEnabled && (
              <div className={classes.subsection}>
                <QBTypography variant="h6" color="secondary" ref={REFS.offers}>
                  {Messages.LABEL.BONDS_FOR_SALE}:
                </QBTypography>

                <QBTypography className={classes.paragraph}>
                  {Messages.MESSAGE.DISCLAIMERS[Constants.TRADE_TYPE.BONDS_FOR_SALE].EXTENDED}
                </QBTypography>
              </div>
            )}
          </div>
          <div className={classes.section}>
            <QBTypography className={classes.sectionTitle} variant="h3" color="primary" gutterBottom>
              Credit Ratings
            </QBTypography>

            <div className={classes.subsection}>
              <QBTypography variant="h6" color="secondary">
                Moody's Rating
              </QBTypography>

              <QBTypography className={classes.paragraph} component="div">
                {Messages.MESSAGE.DISCLAIMERS.MOODYS}
              </QBTypography>
            </div>

            <div className={classes.subsection}>
              <QBTypography variant="h6" color="secondary">
                S&P Rating
              </QBTypography>
              <QBTypographyParagraphs textArray={Messages.MESSAGE.DISCLAIMERS.SANDP} />
            </div>
          </div>
          <div className={classes.section}>
            <QBTypography className={classes.sectionTitle} variant="h3" color="primary" gutterBottom>
              Data Providers
            </QBTypography>

            <QBTypography className={classes.paragraph} component="div">
              {Messages.MESSAGE.DISCLAIMERS.DATAPROVIDERS}
            </QBTypography>
          </div>
          <div className={classes.section}>
            <QBTypography className={classes.sectionTitle} variant="h3" color="primary" gutterBottom>
              Supported Browsers
            </QBTypography>

            <QBTypography className={classes.paragraph} component="div">
              The {Messages.BRANDING.APPLICATION_NAME} application is supported for the following browsers:
              <ul>
                <li>Chrome 87+</li>
                <li>Safari 13.1+</li>
                <li>Firefox 90+</li>
                <li>Edge 92+</li>
                <li>Opera 77+</li>
              </ul>
              While other browsers may be used with {Messages.BRANDING.APPLICATION_NAME}, complete
              functionality cannot be assured.
            </QBTypography>
          </div>
        </Div>
      </MainPaper>
    </Div>
  );
};

export default HelpView;
